/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { BoundingBox, CoordinateSpace, CoordinateSpaceCombiner, isChannelDimension, isLocalDimension, TrackableCoordinateSpace, WatchableCoordinateSpaceTransform } from 'neuroglancer/coordinate_transform';
import { ManagedUserLayer, registerLayerType, registerLayerTypeDetector, registerVolumeLayerType, UserLayer, UserLayerSelectionState } from 'neuroglancer/layer';
import { LoadedDataSubsource, LoadedLayerDataSource } from 'neuroglancer/layer_data_source';
import { Overlay } from 'neuroglancer/overlay';
import { getChannelSpace } from 'neuroglancer/render_coordinate_transform';
import { RenderScaleHistogram, trackableRenderScaleTarget } from 'neuroglancer/render_scale_statistics';
import { DataType, VolumeType } from 'neuroglancer/sliceview/volume/base';
import { MultiscaleVolumeChunkSource } from 'neuroglancer/sliceview/volume/frontend';
import { defineImageLayerShader, getTrackableFragmentMain, ImageRenderLayer } from 'neuroglancer/sliceview/volume/image_renderlayer';
import { trackableAlphaValue } from 'neuroglancer/trackable_alpha';
import { trackableBlendModeValue } from 'neuroglancer/trackable_blend';
import { TrackableBoolean } from 'neuroglancer/trackable_boolean';
import { makeCachedDerivedWatchableValue, makeCachedLazyDerivedWatchableValue, registerNested, WatchableValue, WatchableValueInterface } from 'neuroglancer/trackable_value';
import { UserLayerWithAnnotationsMixin } from 'neuroglancer/ui/annotations';
import { setClipboard } from 'neuroglancer/util/clipboard';
import { Borrowed, RefCounted } from 'neuroglancer/util/disposable';
import { makeValueOrError } from 'neuroglancer/util/error';
import { verifyOptionalObjectProperty } from 'neuroglancer/util/json';
import { VolumeRenderingRenderLayer } from 'neuroglancer/volume_rendering/volume_render_layer';
import { makeWatchableShaderError, ParameterizedShaderGetterResult } from 'neuroglancer/webgl/dynamic_shader';
import { setControlsInShader, ShaderControlsBuilderState, ShaderControlState } from 'neuroglancer/webgl/shader_ui_controls';
import { ChannelDimensionsWidget } from 'neuroglancer/widget/channel_dimensions_widget';
import { makeCopyButton } from 'neuroglancer/widget/copy_button';
import { DependentViewContext } from 'neuroglancer/widget/dependent_view_widget';
import { makeHelpButton } from 'neuroglancer/widget/help_button';
import { addLayerControlToOptionsTab, LayerControlDefinition, registerLayerControl } from 'neuroglancer/widget/layer_control';
// import { enumLayerControl } from 'neuroglancer/widget/layer_control_enum';
import { rangeLayerControl } from 'neuroglancer/widget/layer_control_range';
import { makeMaximizeButton } from 'neuroglancer/widget/maximize_button';
import { renderScaleLayerControl } from 'neuroglancer/widget/render_scale_widget';
import { ShaderCodeWidget } from 'neuroglancer/widget/shader_code_widget';
import { LegendShaderOptions, registerLayerShaderControlsTool, ShaderControls } from 'neuroglancer/widget/shader_controls';
import { Tab } from 'neuroglancer/widget/tab_view';
import { allToolsButton, local_host_SN_flag, viewer, _param as local_href_param, getRegisterLayer, getWorkflowParam } from 'src/main';
import './image_user_layer.less';
import '../neuroglancer/ui/common_style.less'
import { makeElement } from './ui/layer_gene_table_tab';
import { removeChildren, removeFromParent } from './util/dom';
import { mat4, vec4 } from './util/geom';
import { responseJson } from './util/http_request';
import { cancellableFetchSpecialOk } from './util/special_protocol_request';
import { disableStatus, deleteSignpoint, isSelectView, isToolControl } from './viewer';
import { CoordinateSpaceTransformWidget } from './widget/coordinate_transform';
import { message } from './widget/message';
import svg_filp from 'neuroglancer/static/jingxiang_0.svg';
import svg_rotate from 'neuroglancer/static/xuanzhuan_0.svg';
import svg_turning from 'neuroglancer/static/turning.svg';
import svg_shang from 'neuroglancer/static/shang.svg'
import { RangeWidget } from './widget/range';
import { makeSingleLineAnnotation } from './annotation';
// import { RadioColorWidget } from './widget/color';
// import { makeSinglePointAnnotation } from 'src/neuroglancer/annotation/index';
// import { packColor } from './util/color';
// import { AnnotationUserLayer } from './annotation/user_layer';
import { packColor } from './util/color';
import {vec3} from "gl-matrix";
import i18n from './i18n/i18n';
import { registerLassoOpenDialog } from './ui/register_lasso_open_dialog';
import { getData } from './util/comm_fun_hq';
const OPACITY_JSON_KEY = 'opacity';
const BLEND_JSON_KEY = 'blend';
const SHADER_JSON_KEY = 'shader';
const SHADER_CONTROLS_JSON_KEY = 'shaderControls';
const CROSS_SECTION_RENDER_SCALE_JSON_KEY = 'crossSectionRenderScale';
const CHANNEL_DIMENSIONS_JSON_KEY = 'channelDimensions';
const VOLUME_RENDERING_JSON_KEY = 'volumeRendering';
const VOLUME_RENDER_SCALE_JSON_KEY = 'volumeRenderScale';
let transformcount = 0;

const processTags = ['saw-st:v6', 'saw-st:v7', 'pt:v1', 'SAW_spatial_scRNA_V1'];

export interface ImageLayerSelectionState extends UserLayerSelectionState {
  value: any;
}

const Base = UserLayerWithAnnotationsMixin(UserLayer);
export class ImageUserLayer extends Base {
  specification: any;
  opacity = trackableAlphaValue(0.5);
  blendMode = trackableBlendModeValue();
  fragmentMain = getTrackableFragmentMain();
  shaderError = makeWatchableShaderError();
  dataType = new WatchableValue<DataType | undefined>(undefined);
  sliceViewRenderScaleHistogram = new RenderScaleHistogram();
  sliceViewRenderScaleTarget = trackableRenderScaleTarget(1);
  volumeRenderingRenderScaleHistogram = new RenderScaleHistogram();
  gray_level_elbow = new WatchableValue(0);
  min_gray_level = new WatchableValue(0);
  max_gray_level = new WatchableValue(0);
  min_mid_filter = new WatchableValue(0);
  max_mid_filter = new WatchableValue(0);
  // unused
  volumeRenderingRenderScaleTarget = trackableRenderScaleTarget(1);

  channelCoordinateSpace = new TrackableCoordinateSpace();
  channelCoordinateSpaceCombiner =
    new CoordinateSpaceCombiner(this.channelCoordinateSpace, isChannelDimension);
  channelSpace = this.registerDisposer(makeCachedLazyDerivedWatchableValue(
    channelCoordinateSpace => makeValueOrError(() => getChannelSpace(channelCoordinateSpace)),
    this.channelCoordinateSpace));
  volumeRendering = new TrackableBoolean(false, false);

  shaderControlState = this.registerDisposer(new ShaderControlState(
    this.fragmentMain,
    this.registerDisposer(makeCachedDerivedWatchableValue(
      (dataType: DataType | undefined, channelCoordinateSpace: CoordinateSpace, max_gray_level: number | undefined, max_mid_filter: number | undefined) => {
        if (dataType === undefined) return null;
        if (max_gray_level === undefined) max_gray_level = 0;
        if (max_mid_filter === undefined) max_mid_filter = 0;
        return { imageData: { dataType, channelRank: channelCoordinateSpace.rank, max_gray_level, genename: this.managedLayer.genename } };
      },
      [this.dataType, this.channelCoordinateSpace, this.max_gray_level, this.max_mid_filter],
      (a, b) => JSON.stringify(a) === JSON.stringify(b))),
    this.channelCoordinateSpaceCombiner));

  markLoading() {
    const baseDisposer = super.markLoading();
    const channelDisposer = this.channelCoordinateSpaceCombiner.retain();
    return () => {
      baseDisposer();
      channelDisposer();
    };
  }

  addCoordinateSpace(coordinateSpace: WatchableValueInterface<CoordinateSpace>) {
    const baseBinding = super.addCoordinateSpace(coordinateSpace);
    const channelBinding = this.channelCoordinateSpaceCombiner.bind(coordinateSpace);
    return () => {
      baseBinding();
      channelBinding();
    };
  }

  selectionState: ImageLayerSelectionState;

  constructor(managedLayer: Borrowed<ManagedUserLayer>) {
    super(managedLayer);
    this.localCoordinateSpaceCombiner.includeDimensionPredicate = isLocalDimension;
    this.blendMode.changed.add(this.specificationChanged.dispatch);
    this.opacity.changed.add(this.specificationChanged.dispatch);
    this.fragmentMain.changed.add(this.specificationChanged.dispatch);
    this.shaderControlState.changed.add(this.specificationChanged.dispatch);
    this.sliceViewRenderScaleTarget.changed.add(this.specificationChanged.dispatch);
    this.volumeRendering.changed.add(this.specificationChanged.dispatch);
    this.tabs.add(
      'rendering',
      { label: 'Rendering', order: -100, getter: () => new RenderingOptionsTab(this) });
    this.tabs.default = 'rendering';
  }

  activateDataSubsources(subsources: Iterable<LoadedDataSubsource>) {
    let dataType: DataType | undefined;
    for (const loadedSubsource of subsources) {
      if (this.addStaticAnnotations(loadedSubsource)) continue;
      const { subsourceEntry } = loadedSubsource;
      const { subsource } = subsourceEntry;
      const { volume } = subsource;
      if (!(volume instanceof MultiscaleVolumeChunkSource)) {
        loadedSubsource.deactivate('Not compatible with image layer');
        continue;
      }
      if (dataType && volume.dataType !== dataType) {
        loadedSubsource.deactivate(`Data type must be ${DataType[volume.dataType].toLowerCase()}`);
        continue;
      }
      dataType = volume.dataType;
      this.max_gray_level.value = volume.info.max_gray_level;
      this.min_gray_level.value = volume.info.min_gray_level;
      this.min_mid_filter.value = volume.info.min_mid_filter;
      this.max_mid_filter.value = volume.info.max_mid_filter;
      this.gray_level_elbow.value = volume.info.gray_level_elbow;
      loadedSubsource.activate(context => {
        loadedSubsource.addRenderLayer(new ImageRenderLayer(volume, {
          opacity: this.opacity,
          opacityFactor: viewer.imgOpacityFactor,
          imgFadeNeeded: viewer.imgFadeNeeded,
          blendMode: this.blendMode,
          shaderControlState: this.shaderControlState,
          shaderError: this.shaderError,
          transform: loadedSubsource.getRenderLayerTransform(this.channelCoordinateSpace),
          renderScaleTarget: this.sliceViewRenderScaleTarget,
          renderScaleHistogram: this.sliceViewRenderScaleHistogram,
          localPosition: this.localPosition,
          channelCoordinateSpace: this.channelCoordinateSpace,
          emptyChunks: viewer.emptyChunks[this.managedLayer.name],
          genename: loadedSubsource.loadedDataSource.layer.genename,
        }));
        const volumeRenderLayer = context.registerDisposer(new VolumeRenderingRenderLayer({
          multiscaleSource: volume,
          shaderControlState: this.shaderControlState,
          shaderError: this.shaderError,
          transform: loadedSubsource.getRenderLayerTransform(this.channelCoordinateSpace),
          renderScaleTarget: this.volumeRenderingRenderScaleTarget,
          renderScaleHistogram: this.volumeRenderingRenderScaleHistogram,
          localPosition: this.localPosition,
          channelCoordinateSpace: this.channelCoordinateSpace,
          emptyChunks: viewer.emptyChunks[this.managedLayer.name]
        }));
        context.registerDisposer(loadedSubsource.messages.addChild(volumeRenderLayer.messages));
        context.registerDisposer(registerNested((context, volumeRendering) => {
          if (!volumeRendering) return;
          context.registerDisposer(this.addRenderLayer(volumeRenderLayer.addRef()));
        }, this.volumeRendering));
        this.shaderError.changed.dispatch();
      });
    }
    this.dataType.value = dataType;
  }

  restoreState(specification: any) {
    super.restoreState(specification);
    this.specification = specification
    this.opacity.restoreState(specification[OPACITY_JSON_KEY]);
    verifyOptionalObjectProperty(
      specification, BLEND_JSON_KEY, blendValue => this.blendMode.restoreState(blendValue));
    this.fragmentMain.restoreState(specification[SHADER_JSON_KEY]);
    this.shaderControlState.restoreState(specification[SHADER_CONTROLS_JSON_KEY]);
    this.sliceViewRenderScaleTarget.restoreState(
      specification[CROSS_SECTION_RENDER_SCALE_JSON_KEY]);
    this.channelCoordinateSpace.restoreState(specification[CHANNEL_DIMENSIONS_JSON_KEY]);
    this.volumeRendering.restoreState(specification[VOLUME_RENDERING_JSON_KEY]);
  }
  toJSON() {
    const x = super.toJSON();
    x[OPACITY_JSON_KEY] = this.opacity.toJSON();
    x[BLEND_JSON_KEY] = this.blendMode.toJSON();
    x[SHADER_JSON_KEY] = this.fragmentMain.toJSON();
    x[SHADER_CONTROLS_JSON_KEY] = this.shaderControlState.toJSON();
    x[CROSS_SECTION_RENDER_SCALE_JSON_KEY] = this.sliceViewRenderScaleTarget.toJSON();
    x[CHANNEL_DIMENSIONS_JSON_KEY] = this.channelCoordinateSpace.toJSON();
    x[VOLUME_RENDERING_JSON_KEY] = this.volumeRendering.toJSON();
    return x;
  }

  displayImageSelectionState(state: this['selectionState'], parent: HTMLElement): boolean {
    const { value } = state;
    if (value == null) return false;
    const channelSpace = this.channelSpace.value;
    if (channelSpace.error !== undefined) return false;
    const { numChannels, coordinates, channelCoordinateSpace: { names, rank } } = channelSpace;
    const grid = document.createElement('div');
    grid.classList.add('neuroglancer-selection-details-value-grid');
    let gridTemplateColumns = '[copy] 0fr ';
    if (rank !== 0) {
      gridTemplateColumns += `repeat(${rank}, [dim] 0fr [coord] 0fr) `;
    }
    gridTemplateColumns += `[value] 1fr`;
    grid.style.gridTemplateColumns = gridTemplateColumns;
    for (let channelIndex = 0; channelIndex < numChannels; ++channelIndex) {
      const x = rank === 0 ? value : value[channelIndex];
      // TODO(jbms): do data type-specific formatting
      const valueString = x == null ? '' : x.toString();
      const copyButton = makeCopyButton({
        title: `Copy value`,
        onClick: () => {
          setClipboard(valueString);
        },
      });
      grid.appendChild(copyButton);
      for (let channelDim = 0; channelDim < rank; ++channelDim) {
        const dimElement = document.createElement('div');
        dimElement.classList.add('neuroglancer-selection-details-value-grid-dim');
        dimElement.textContent = names[channelDim];
        grid.appendChild(dimElement);
        const coordElement = document.createElement('div');
        coordElement.classList.add('neuroglancer-selection-details-value-grid-coord');
        coordElement.textContent = coordinates[channelIndex * rank + channelDim].toString();
        grid.appendChild(coordElement);
      }
      const valueElement = document.createElement('div');
      valueElement.classList.add('neuroglancer-selection-details-value-grid-value');
      valueElement.textContent = valueString;
      grid.appendChild(valueElement);
    }
    parent.appendChild(grid);
    return true;
  }

  displaySelectionState(
    state: this['selectionState'], parent: HTMLElement, context: DependentViewContext): boolean {
    let displayed = this.displayImageSelectionState(state, parent);
    if (super.displaySelectionState(state, parent, context)) displayed = true;
    return displayed;
  }

  getLegendShaderOptions(): LegendShaderOptions {
    return {
      memoizeKey: `ImageUserLayer`,
      parameters: this.shaderControlState.builderState,
      // fixme: support fallback
      encodeParameters: p => p.key,
      defineShader: (builder, shaderBuilderState: ShaderControlsBuilderState) => {
        builder.addFragmentCode(`
 #define uOpacity 1.0
 `);
        defineImageLayerShader(builder, shaderBuilderState);
      },
      initializeShader:
        (shaderResult: ParameterizedShaderGetterResult<ShaderControlsBuilderState>) => {
          const shader = shaderResult.shader!;
          setControlsInShader(
            this.manager.root.display.gl, shader, this.shaderControlState,
            shaderResult.parameters.parseResult.controls);
        },
    };
  }

  static type = 'image';
  static typeAbbreviation = 'img';
}

export function makeShaderCodeWidget(layer: ImageUserLayer) {
  return new ShaderCodeWidget({
    shaderError: layer.shaderError,
    fragmentMain: layer.fragmentMain,
    shaderControlState: layer.shaderControlState,
  });
}

const LAYER_CONTROLS: LayerControlDefinition<ImageUserLayer>[] = [
  //  {
  //    label: 'Resolution (slice)',
  //    toolJson: CROSS_SECTION_RENDER_SCALE_JSON_KEY,
  //    ...renderScaleLayerControl(layer => ({
  //                                 histogram: layer.sliceViewRenderScaleHistogram,
  //                                 target: layer.sliceViewRenderScaleTarget
  //                               })),
  //  },
  //  {
  //    label: 'Blending',
  //    toolJson: BLEND_JSON_KEY,
  //    ...enumLayerControl(layer => layer.blendMode),
  //  },
  // {
  //   label: 'Volume rendering (experimental)',
  //   toolJson: VOLUME_RENDERING_JSON_KEY,
  //   ...checkboxLayerControl(layer => layer.volumeRendering),
  // },
  {
    label: 'Resolution (3d)',
    toolJson: VOLUME_RENDER_SCALE_JSON_KEY,
    isValid: layer => layer.volumeRendering,
    ...renderScaleLayerControl(layer => ({
      histogram: layer.volumeRenderingRenderScaleHistogram,
      target: layer.volumeRenderingRenderScaleTarget
    })),
  },
  {
    label: 'Opacity',
    toolJson: OPACITY_JSON_KEY,
    ...rangeLayerControl(layer => ({ value: layer.opacity })),
  },
];

// 小图微调
const drive = [{ id: 'move-up', title: 'move-up' },
{ id: 'move-right', title: 'move-right' },
{ id: 'move-down', title: 'move-down' },
{ id: 'move-left', title: 'move-left' }];
//  路径
// const regist_path: any = {
//   'BB': '',
//   'AAAA': '',
//   'BAAAA': 'B',
//   'BAB': 'AAA',
//   'BAAB': 'AA',
//   'BAAAB': 'A',
//   'AAAAB': 'B',
//   'AAAB': 'BA',
//   'AAB': 'BAA',
//   'AB': 'BAAA'
// }
// let path: string = '';
// 手动配准
let registFlipCount = 0;
// let registRotateCount = 0;
export let registParam = {
  OffsetX: 0,
  OffsetY: 0,
  deg: 0,
  filp: 0,
  isFineTurning: false,
  scalex: 1,
  scaley: 1,
  bound:{}
}
// 获取outputDimension 中的单位以及数值
for (const control of LAYER_CONTROLS) {
  registerLayerControl(ImageUserLayer, control);
}

export class SelectImageType extends RefCounted {
  selectBinContainer = makeElement('div', ['selectBinContainer']);
  selectElement = document.createElement('select');
  constructor(layer: ImageUserLayer) {
    super();
    const { selectBinContainer, selectElement } = this;
    let label = makeElement('label', ['selectBinLabel'], {}, `Please select image type：`);
    selectElement.classList.add('neuroglancer-image-dropdown-select-image-type');
    this.registerDisposer(() => removeFromParent(selectBinContainer));
    const layerSource = layer.dataSources[0].spec_.url;
    let imageType = this.getImageType(layerSource);
    if (viewer.allDataSource.imageType?.length > 1) {
      for (let i = 0; i < viewer.allDataSource.imageType.length; i++) {
        selectElement.options.add(new Option(viewer.allDataSource.imageType[i].type, viewer.allDataSource.imageType[i].type, false, viewer.allDataSource.imageType[i].type === imageType ? true : false));
      }
    } else {
      selectElement.style.display = 'none';
      label.style.display = 'none';
    }
    selectBinContainer.appendChild(label);
    selectBinContainer.appendChild(selectElement);
  }
  getImageType(url: string) {
    let _url = url.split('/');
    return _url[_url.length - 1];
  }
  onchange = (url: string) => {
    let objS = this.selectElement.value;
    // 判断选择的imagetype是否有多通道
    let shader = '';
    const isChannel = viewer.allDataSource.imageType.filter((item: { [K: string]: string }) => {
      return item.type === objS;
    });
    if (isChannel[0].num_channels !== 1) {
      shader = `#uicontrol vec3 color color(default="#E26EF2")
      #uicontrol float brightness slider(min=-1, max=1)
      #uicontrol float contrast slider(min=-3, max=3, step=0.01)
      void main() {
        emitRGB(vec3(toNormalized(getDataValue(0)),
                     toNormalized(getDataValue(1)),
                     toNormalized(getDataValue(2))));
      }`;
    } else {
      shader = `#uicontrol invlerp normalized
      void main() {
        emitGrayscale(normalized());
      }`
    }
    const imageType = this.getImageType(url);
    const newUrl = url.replace(imageType, objS);
    // urlInput.setValueAndSelection(this.lastParam);
    // urlInput.disableCompletion();
    // urlInput.hideCompletions();
    // urlInput.onCommit.dispatch(this.lastParam, true);
    let json = viewer.state.toJSON();
    for (let i = 0; i < json.layers.length; i++) {
      if (json.layers[i].name === json.selectedLayer.layer) {
        json.layers[i].source = newUrl;
        json.layers[i].shader = shader;
      }
    }
    json.selectedLayer.layer = json.selectedLayer.layer.replace(imageType, objS);
    viewer.state.restoreState(json);
  }
}

export class changeChannel extends RefCounted {
  selectBinContainer = makeElement('div', ['select-channel-container']);
  selectElement = document.createElement('select');
  img_layer: ImageUserLayer;
  constructor(layer: ImageUserLayer, numChannel: number) {
    super();
    this.img_layer = layer;
    const { selectBinContainer, selectElement } = this;
    let label = makeElement('label', ['selectBinLabel'], {}, `Please select image channel`);
    selectElement.classList.add('neuroglancer-image-dropdown-select-image-type');
    this.registerDisposer(() => removeFromParent(selectBinContainer));
    let codeWidget: ShaderCodeWidget | string = this.registerDisposer(makeShaderCodeWidget(layer));
    codeWidget = codeWidget.textEditor.getValue();
    let which_channel;
    if (codeWidget.includes('getDataValue(0)') && codeWidget.includes('getDataValue(1)') && codeWidget.includes('getDataValue(2)')) {
      which_channel = 'all';
    } else if (codeWidget.includes('getDataValue(0)')) {
      which_channel = 'channel_1';
    } else if (codeWidget.includes('getDataValue(1)')) {
      which_channel = 'channel_2';
    } else if (codeWidget.includes('getDataValue(2)')) {
      which_channel = 'channel_3';
    }
    selectElement.options.add(new Option('all', 'all', false, 'all' === which_channel ? true : false));
    for (let i = 1; i <= numChannel; i++) {
      selectElement.options.add(new Option('channel_' + i, 'channel_' + i, false, 'channel_' + i === which_channel ? true : false));
    }
    selectBinContainer.appendChild(label);
    selectBinContainer.appendChild(selectElement);
  }
  onchange = () => {
    let objS = this.selectElement.value;
    let codeWidget: ShaderCodeWidget | string | Array<number | string | {}> = this.registerDisposer(makeShaderCodeWidget(this.img_layer));
    let newcodeWidget = codeWidget.textEditor.getValue().split('\n');;
    newcodeWidget.splice(4, newcodeWidget.length - 4);
    if (objS === 'all') {
      newcodeWidget.push(`emitRGB(vec3(toNormalized(getDataValue(0)),
      toNormalized(getDataValue(1)),
      toNormalized(getDataValue(2))));}`)
    } else if (objS === 'channel_1') {
      newcodeWidget.push(`emitRGB(color *
        (toNormalized(getDataValue(0)) + brightness) *
        exp(contrast));}`)
    } else if (objS === 'channel_2') {
      newcodeWidget.push(`emitRGB(color *
        (toNormalized(getDataValue(1)) + brightness) *
        exp(contrast));}`)
    } else if (objS === 'channel_3') {
      newcodeWidget.push(`emitRGB(color *
        (toNormalized(getDataValue(2)) + brightness) *
        exp(contrast));}`)
    }
    codeWidget.textEditor.setValue(newcodeWidget.join(' \n').toString());
    this.img_layer.managedLayer.shader = newcodeWidget.join(' \n').toString();
  }
}

export class RenderingOptionsTab extends Tab {
  codeWidget = this.registerDisposer(makeShaderCodeWidget(this.layer));
  bindings = this.layer.channelCoordinateSpaceCombiner.bindings;
  layer_bound_o:BoundingBox;
  flipMat = mat4.fromValues(-1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1)
  rotMat = mat4.fromValues(0, -1, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1)
  filp = makeElement('div', ['neuroglancer-icon', 'neuroglancer-checkbox-icon', 'dark-background', 'neuroglancer-transform-filp'], { 'title': i18n.registrationPanelFile, 'style': 'margin-bottom: 10px;margin-right: 10px' }, svg_filp);      //  翻转按钮
  rotate = makeElement('div', ['neuroglancer-icon', 'neuroglancer-checkbox-icon', 'dark-background', 'neuroglancer-transform-rotate'], { 'title': i18n.registrationPanelRotate90, 'style': 'margin-bottom: 10px;' }, svg_rotate);  //  旋转按钮
  displacement_num_div = makeElement('div', ['neuroglancer-transform-displace-num']);  //  移动步进
  displacement_num_driveDiv = makeElement('div', [], { 'style': 'margin:1rem 0;justify-content: center;height: 11rem' });  //  移动方向按钮
  displacement_save = makeElement('div', ['neuroglancer-transform-submit', 'neuroglancer-icon', 'async_submit_btn_loading'],{},
  `<i class="submit_btn_el-icon-loading"><svg t="1687856555440" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="1917" width="14" height="14"><path d="M511.882596 287.998081h-0.361244a31.998984 31.998984 0 0 1-31.659415-31.977309v-0.361244c0-0.104761 0.115598-11.722364 0.115598-63.658399V96.000564a31.998984 31.998984 0 1 1 64.001581 0V192.001129c0 52.586273-0.111986 63.88237-0.119211 64.337537a32.002596 32.002596 0 0 1-31.977309 31.659415zM511.998194 959.99842a31.998984 31.998984 0 0 1-31.998984-31.998984v-96.379871c0-51.610915-0.111986-63.174332-0.115598-63.286318s0-0.242033 0-0.361243a31.998984 31.998984 0 0 1 63.997968-0.314283c0 0.455167 0.11921 11.711527 0.11921 64.034093v96.307622a31.998984 31.998984 0 0 1-32.002596 31.998984zM330.899406 363.021212a31.897836 31.897836 0 0 1-22.866739-9.612699c-0.075861-0.075861-8.207461-8.370021-44.931515-45.094076L195.198137 240.429485a31.998984 31.998984 0 0 1 45.256635-45.253022L308.336112 263.057803c37.182834 37.182834 45.090463 45.253022 45.41197 45.578141A31.998984 31.998984 0 0 1 330.899406 363.021212zM806.137421 838.11473a31.901448 31.901448 0 0 1-22.628318-9.374279L715.624151 760.859111c-36.724054-36.724054-45.018214-44.859267-45.097687-44.93874a31.998984 31.998984 0 0 1 44.77618-45.729864c0.32512 0.317895 8.395308 8.229136 45.578142 45.411969l67.88134 67.88134a31.998984 31.998984 0 0 1-22.624705 54.630914zM224.000113 838.11473a31.901448 31.901448 0 0 0 22.628317-9.374279l67.88134-67.88134c36.724054-36.724054 45.021826-44.859267 45.097688-44.93874a31.998984 31.998984 0 0 0-44.776181-45.729864c-0.32512 0.317895-8.395308 8.229136-45.578142 45.411969l-67.88134 67.884953a31.998984 31.998984 0 0 0 22.628318 54.627301zM255.948523 544.058589h-0.361244c-0.104761 0-11.722364-0.115598-63.658399-0.115598H95.942765a31.998984 31.998984 0 1 1 0-64.00158h95.996952c52.586273 0 63.88237 0.111986 64.337538 0.11921a31.998984 31.998984 0 0 1 31.659414 31.97731v0.361244a32.002596 32.002596 0 0 1-31.988146 31.659414zM767.939492 544.058589a32.002596 32.002596 0 0 1-31.995372-31.666639v-0.361244a31.998984 31.998984 0 0 1 31.659415-31.970085c0.455167 0 11.754876-0.11921 64.34115-0.11921h96.000564a31.998984 31.998984 0 0 1 0 64.00158H831.944685c-51.936034 0-63.553638 0.111986-63.665624 0.115598h-0.335957zM692.999446 363.0176a31.998984 31.998984 0 0 1-22.863126-54.381656c0.317895-0.32512 8.229136-8.395308 45.41197-45.578141l67.88134-67.884953A31.998984 31.998984 0 1 1 828.693489 240.429485l-67.892177 67.88134c-31.020013 31.023625-41.644196 41.759794-44.241539 44.393262l-0.697201 0.722488a31.908673 31.908673 0 0 1-22.863126 9.591025z" fill="#ffffff" p-id="1918"></path></svg></i>${i18n.if_submit}`);
  transform: WatchableCoordinateSpaceTransform;
  transformWidget: CoordinateSpaceTransformWidget;
  transformUI = makeElement('div', ['transform-v3-ui']);
  registerLayer = makeElement('div',['neuroglancer-register-layer']);
  default_color_arr = [{ id: 0, color: '#D20000' }, { id: 1, color: '#FF0000' }, { id: 2, color: '#FFBE00' }, { id: 3, color: '#FEFF00' },
  { id: 4, color: '#7ED332' }, { id: 5, color: '#00B442' }, { id: 6, color: '#00B2F6' }, { id: 7, color: '#0070C6' }, { id: 8, color: '#001F64' }, { id: 9, color: '#7A26A6' },
  ];
  defaultColor = document.createElement('div');
  ifRegisterDom = makeElement('div',['neuroglancer-ifRegister-dom']);
  scaleDom = makeElement('div',['neuroglancer-scale-dom']);
  isReady = false;
  operateDom = makeElement('div',[],{style: 'display: flex;justify-content: space-between;'});
  rotateVal = new WatchableValue(0);
  cur_selectedLayer: ImageUserLayer = this.layer;
  imageComplete: boolean = false;
  constructor(public layer: ImageUserLayer) {
    super();
    const { element } = this;
    element.classList.add('neuroglancer-image-dropdown');
    for (const control of LAYER_CONTROLS) {
      if(document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'true')continue
      element.appendChild(addLayerControlToOptionsTab(this, layer, this.visibility, control));
    }
    let spacer = document.createElement('div');
    spacer.style.flex = '1';
    let topRow = document.createElement('div');
    topRow.className = 'neuroglancer-image-dropdown-top-row';
    topRow.appendChild(document.createTextNode('Shader'));
    topRow.appendChild(spacer);
    topRow.appendChild(makeMaximizeButton({
      title: 'Show larger editor view',
      onClick: () => {
        new ShaderCodeOverlay(this.layer);
      }
    }));
    topRow.appendChild(makeHelpButton({
      title: 'Documentation on image layer rendering',
      href:
        'https://github.com/google/neuroglancer/blob/master/src/neuroglancer/sliceview/image_layer_rendering.md',
    }));
    element.appendChild(topRow);
    element.appendChild(
      this.registerDisposer(new ChannelDimensionsWidget(layer.channelCoordinateSpaceCombiner))
        .element);
    element.appendChild(this.codeWidget.element);
    element.appendChild(
      this
        .registerDisposer(new ShaderControls(
          layer.shaderControlState, this.layer.manager.root.display, this.layer, {
          visibility: this.visibility,
          legendShaderOptions: this.layer.getLegendShaderOptions(),
        }))
        .element);
    // 添加调整开关
    this.initTransformUI();
    this.transformUI.addEventListener('mouseover', ()=>{
      if(!viewer.selectedLayer.layer || !viewer.selectedLayer.layer.genetype.includes('transform')){
        viewer.selectedLayer.layer = undefined;
        viewer.selectedLayer.layer = viewer.layerManager.managedLayers.filter((item:any)=> item.genetype === `transform`)[0];
      }
    });
  }
  initTransformUI() {
    if(document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'true'){
      this.selectRegisterLayer();
      this.displacement_num();
      this.Manual_registration();
      this.transformUI.appendChild(this.element)
      this.IFRegister();
      this.transfomr_reset();
      this.displace_move();
      this.transformUI.appendChild(this.operateDom);
      registParam = {
        OffsetX: 0,
        OffsetY: 0,
        filp: 0,
        deg: 0,
        scalex: 1,
        scaley: 1,
        isFineTurning: false,
        bound: {}
      }
      registFlipCount = 0;
      if(this.cur_selectedLayer.dataSources[0]?.loadState_){
        if((this.cur_selectedLayer.dataSources[0].loadState_ as LoadedLayerDataSource).transform.value.rank === 3)(this.cur_selectedLayer.dataSources[0].loadState_ as LoadedLayerDataSource).transform.transform = new Float64Array(mat4.create());
        if((this.cur_selectedLayer.dataSources[0].loadState_ as LoadedLayerDataSource).transform.value.rank === 4)(this.cur_selectedLayer.dataSources[0].loadState_ as LoadedLayerDataSource).transform.transform = new Float64Array([1,0,0,0,0,0,1,0,0,0,0,0,1,0,0,0,0,0,1,0,0,0,0,0,1]);
      }
      const timer1 = setInterval(() => {
        // 添加特征点十字线
        if(!((this.cur_selectedLayer as ImageUserLayer).dataSources[0].loadState as LoadedLayerDataSource)?.error && !this.imageComplete){
          const source:LoadedDataSubsource = ((this.cur_selectedLayer as ImageUserLayer).dataSources[0].loadState as LoadedLayerDataSource)?.subsources[2];
          if(source?.subsourceEntry?.subsource.staticAnnotations?.annotationMap.size as number < 1){
            const bounds = this.getImageBoun()!;
            registParam.bound = bounds;
            const transform_o_x = 0.5 * (bounds.upperBounds[0] + bounds.lowerBounds[0]);
            const transform_o_y = 0.5 * (bounds.upperBounds[1] + bounds.lowerBounds[1]);  
            viewer.transform_sign_point = vec3.fromValues(transform_o_x,transform_o_y,0);
            const linex = makeSingleLineAnnotation(
              new Float32Array([transform_o_x - 5000, transform_o_y, 0]), 
              new Float32Array([transform_o_x + 5000, transform_o_y, 0]), 'line_x');
            linex.properties = [packColor(vec4.fromValues(1.0, 0.0, 0.0, 1.0))];
            // linex.properties = [0,0,1]
            const liney = makeSingleLineAnnotation(
              new Float32Array([transform_o_x, transform_o_y - 5000, 0]), 
              new Float32Array([transform_o_x, transform_o_y + 5000, 0]), 'line_y');
            liney.properties = [0,0,1]
            liney.properties = [packColor(vec4.fromValues(0.0, 1.0, 0.0, 1.0))];
            // x 标注
            const arrowx1 = makeSingleLineAnnotation(
              new Float32Array([transform_o_x + 5400, transform_o_y + 200, 0]), 
              new Float32Array([transform_o_x + 5200, transform_o_y - 200, 0]), 'arrowx1');
            arrowx1.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
            const arrowx2 = makeSingleLineAnnotation(
              new Float32Array([transform_o_x + 5200, transform_o_y + 200, 0]), 
              new Float32Array([transform_o_x + 5400, transform_o_y - 200, 0]), 'arrowx2');
            arrowx2.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
            const arrowy1 = makeSingleLineAnnotation(
              new Float32Array([transform_o_x + 100, transform_o_y - 5600, 0]), 
              new Float32Array([transform_o_x, transform_o_y - 5400, 0]), 'arrowy1');
            arrowy1.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
            const arrowy2 = makeSingleLineAnnotation(
              new Float32Array([transform_o_x - 100, transform_o_y - 5600, 0]), 
              new Float32Array([transform_o_x, transform_o_y - 5400, 0]), 'arrowy2');
            arrowy2.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
            const arrowy3 = makeSingleLineAnnotation(
              new Float32Array([transform_o_x, transform_o_y - 5400, 0]), 
              new Float32Array([transform_o_x, transform_o_y - 5200, 0]), 'arrowy3');
            arrowy3.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
            // y 标注
            source.subsourceEntry.subsource.staticAnnotations?.add(linex);
            source.subsourceEntry.subsource.staticAnnotations?.add(liney);
            source.subsourceEntry.subsource.staticAnnotations?.add(arrowx1);
            source.subsourceEntry.subsource.staticAnnotations?.add(arrowx2);
            source.subsourceEntry.subsource.staticAnnotations?.add(arrowy1);
            source.subsourceEntry.subsource.staticAnnotations?.add(arrowy2);
            source.subsourceEntry.subsource.staticAnnotations?.add(arrowy3);
            this.imageComplete = true;
            clearInterval(timer1)
          }
        }
      }, 100);
    }
    // if(document.getElementsByClassName('neuroglancer-icon-IF-control')[0] && document.getElementsByClassName('neuroglancer-icon-IF-control')[0].getAttribute('data-checked') === 'true' && this.cur_selectedLayer.genename.includes(allToolsButton.isIFRegister as string)){
    //   this.Manual_registration('rotate');
    //   this.element.appendChild(this.transformUI)
    // }
  }
  registerMoveUpDown(action: string) {
    transformcount++;
    const transformSize = viewer.transformsize;
    switch (action) {
      case 'move-up':
        registParam.OffsetY = registParam.OffsetY - Number(transformSize);
        break;
      case 'move-down':
        registParam.OffsetY = registParam.OffsetY + Number(transformSize);
        break;
      case 'move-left':
        registParam.OffsetX = registParam.OffsetX - Number(transformSize);
        break;
      case 'move-right':
        registParam.OffsetX = registParam.OffsetX + Number(transformSize);
        break;
    }
    const bound = this.getImageBoun();
    registParam.bound = bound!;
    viewer.newLayerRotate();
  }
  // 手动配准
  Manual_registration(value?: string) {
    const div = makeElement('div', [], { 'style': 'display: flex;justify-content: center' });
    if (value === 'filp') {
      div.appendChild(this.filp);
    } else if (value === 'rotate') {
      div.appendChild(this.rotate);
    } else {
      div.appendChild(this.filp);
      div.appendChild(this.rotate);
    }
    this.transformUI.appendChild(div);
    this.filp.addEventListener('click', () => {
      registFlipCount+=1;
      registFlipCount = registFlipCount % 2;   
      registParam.filp = registParam.filp + 1;
      registParam.filp = registParam.filp % 2;
      const bound = this.getImageBoun()!;
      registParam.bound = bound;
      viewer.newLayerRotate();
    });

    this.rotate.addEventListener('click', () => {
      let deg = (document.getElementsByClassName('if-rotate')[0] as HTMLInputElement).valueAsNumber - 90;
      deg = deg < -180 ? 360 + deg : deg;
      deg = Number(deg.toFixed(2));
      registParam.deg = deg;
      (document.getElementsByClassName('if-rotate')[0] as HTMLInputElement).valueAsNumber = deg;
      this.rotateVal.value = deg;
      const bound = this.getImageBoun();
      registParam.bound = bound!;
    });
  }
  // 添加位移代码
  displacement() {
    if (transformcount < 1) {
      (document.getElementById('neuroglancer-container') as HTMLElement).addEventListener('keyup', (e) => {
        transformcount++;
        if (e.code === 'ArrowUp') {
          this.registerMoveUpDown('move-up');
        } else if (e.code === 'ArrowRight') {
          this.registerMoveUpDown('move-right');
        } else if (e.code === 'ArrowDown') {
          this.registerMoveUpDown('move-down');
        } else if (e.code === 'ArrowLeft') {
          this.registerMoveUpDown('move-left');
        }
      })
    }
  }
  // 添加图像选择
  selectRegisterLayer(){
    const span = makeElement('span', [], {}, i18n.registrationPanelLayer);
    const option = document.createElement('select');
    option.classList.add('register-layer-select','comminput')
    const registerLayerArry = (allToolsButton.isTransform as string[]);
    for (let i = 0; i < registerLayerArry.length; i++) {
      option.add(new Option(registerLayerArry[i][0], registerLayerArry[i][0],false, i === 0))
    }
    option.onchange = ()=>{
      this.initRegisterLayer(option.value);
    }
    this.registerLayer.appendChild(span);
    this.registerLayer.appendChild(option);
    this.ifRegisterDom.appendChild(this.registerLayer);
    this.transformUI.appendChild(this.ifRegisterDom);
  }
  // initRegisterLayer
  initRegisterLayer(registerName: string){
    this.element.innerHTML = '';
    this.isReady = true;
    viewer.clearActiveTool();
    viewer.configStorage.clear();
    viewer.configDialog?.close();
    let _json = viewer.state.toJSON();
    viewer.layerManager.managedLayers.forEach((item)=>{
      if ( item.name.indexOf(registerName) && item.type !== 'annotation') {
        item.setDisabled(true);
      }
    });
    let _layers = viewer.state.toJSON().layers;
    _layers = JSON.parse(JSON.stringify(viewer.layerCloneObj.layer)) ;
    allToolsButton.genelistFile = viewer.layerCloneObj.gene_list_file;
    viewer.state.restoreState(_json);
    for(let j = 0; j < _layers.length; j++){
      if (_layers[j].heatmap) {
        _layers[j].hasData = _layers[j].genename === allToolsButton.genelistFile;
        _layers[j].visible = _layers[j].genename === allToolsButton.genelistFile;
        _layers[j].shaderControls = {
          'oFactor': 0.5
        }
        _layers[j].genecolor = 'Default'
      } else {
        _layers[j].visible = false;
        _layers[j].disabled = true;
      }
    }
    const register = (allToolsButton.isTransform as [string,string, string | number][]).filter((item)=>item[0] === registerName)[0];
    const shader = register[2] as number > 1 ? `#uicontrol vec3 color color(default="#E26EF2")
          #uicontrol float brightness slider(min=-1, max=1)
          #uicontrol float contrast slider(min=-3, max=3, step=0.01)
          void main() {
            emitRGB(
              vec3( (toNormalized(getDataValue(0)) + brightness ) * exp(contrast),
                    (toNormalized(getDataValue(1)) + brightness ) * exp(contrast),
                    (toNormalized(getDataValue(2)) + brightness ) * exp(contrast)));
          }`: `#uicontrol invlerp normalized\n    void main() {\n      emitGrayscale(normalized());\n    }`
    _layers.push({
      hasData: true,
      visible: true,
      source: `precomputed://${local_host_SN_flag}/image/transform/${register[1]}`,
      // source: `precomputed://${local_host_SN_flag}/image/${register[1]}`,
      genetype: 'transform',
      name: `transform_${registerName}`,
      genename: `transform_${registerName}`,
      shader: shader,
      tab: 'rendering',
      type: 'image'
    });
    const isTemplate = (document.getElementsByClassName('neuroglancer-icon-standardAnalysis')[0] as HTMLElement)?.getAttribute('data-checked');
    if(isTemplate === 'true'){
      _layers.push({
        "source": `precomputed://${local_host_SN_flag}/annotation/ipr/bin10`,
        "tab": "rendering",
        "type": "annotation",
        "shader": `#uicontrol float sizeFactor slider(min=0.0, max=4.0, default=1.0, step=0.01); 
        #uicontrol float oFactor slider(min=0.0, max=1.0, default=1.0, step=0.01);
        void main() { 
          setColor(defaultColor());
          setPointMarkerFactor(sizeFactor);
          setPointMarkerOpacityFactor(oFactor - 0.01);
        }`,
        "genename": 'template',
        "name": 'template',
        "hasData": true
      });
    }
    _json.selectedLayer = {
      layer: `transform_${registerName}`,
      visible: true
    }
    _json.layers = _layers;
    viewer.state.restoreState(_json);
    viewer.selectedLayer.layer = viewer.layerManager.managedLayers.filter((item:any)=> item.name === `transform_${registerName}`)[0];
    this.cur_selectedLayer = (viewer.selectedLayer.layer as ManagedUserLayer ).layer as ImageUserLayer;
    (document.getElementsByClassName('transform-size-value')[0] as HTMLInputElement).value = '5';
    (document.getElementsByClassName('if-rotate')[0] as HTMLInputElement).valueAsNumber = 0;
    (document.getElementsByClassName('if-scalex')[0] as HTMLInputElement).valueAsNumber = 0;
    (document.getElementsByClassName('if-scaley')[0] as HTMLInputElement).valueAsNumber = 0;
    registParam.OffsetX = 0;
    registParam.OffsetY = 0;
    registParam.filp = 0;
    registParam.deg = 0;
    registParam.isFineTurning = false;
    registParam.scalex = 1;
    registParam.scaley = 1;
    registParam.bound = this.getImageBoun()!;
    viewer.imageSourceModelMat4 = mat4.create();
    viewer.imageSourceModelMat4Inverse = mat4.create();
    this.rotateVal.value = 0;
    this.element.innerHTML = '';
      for (const control of LAYER_CONTROLS) {
        if(document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'true')continue
        this.element.appendChild(addLayerControlToOptionsTab(this, this.cur_selectedLayer, this.visibility, control));
      }
    this.element.appendChild(
      this
        .registerDisposer(new ShaderControls(
          this.cur_selectedLayer.shaderControlState, this.cur_selectedLayer.manager.root.display, this.cur_selectedLayer, {
          visibility: this.visibility,
          legendShaderOptions: this.cur_selectedLayer.getLegendShaderOptions(),
        }))
        .element);
  }
  // 返回用户选择的配准模式
  getRegisterMode(){
    const param = {
      layer: (document.getElementsByClassName('register-layer-select')[0] as HTMLInputElement).value,
      rotate: (document.getElementsByClassName('if-rotate')[0] as HTMLInputElement).value,
      extrude_x: ((document.getElementsByClassName('if-scalex')[0] as HTMLInputElement).valueAsNumber / 100).toString(),
      extrude_y: ((document.getElementsByClassName('if-scaley')[0] as HTMLInputElement).valueAsNumber / 100).toString()
    }
    return param;
  }
  // 返回image图像的bound
  getImageBoun(){
    let bound:BoundingBox | undefined = undefined;
    if(!viewer.selectedLayer.layer){
      viewer.selectedLayer.layer = viewer.layerManager.managedLayers.filter((item:any)=> item.name === `transform_${(document.getElementsByClassName('register-layer-select')[0] as HTMLInputElement).value}` )[0]
    }
    if((viewer.selectedLayer.layer as ManagedUserLayer ).layer instanceof ImageUserLayer){
      for (const a of ((viewer.selectedLayer.layer as ManagedUserLayer).layer as ImageUserLayer)?.channelCoordinateSpaceCombiner.bindings) {
        bound = a.space.value.boundingBoxes[0].box;
      }
    }
    return bound;
  }
  // 添加IF配准相关
  IFRegister(){
    const rotateDom = makeElement('div',[],{'style': 'margin-bottom: 0.75rem'});
    const span = makeElement('div',[],{'style': 'display: flex;margin: 0.5rem 0;justify-content: space-between;'});
    const text = makeElement('div',['register-scale-text'],{}, `<i title=${i18n.registrationPanelInfo}><svg t="1672814412090" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4998" width="16" height="16"><path d="M512 128a384 384 0 1 0 0 768 384 384 0 0 0 0-768z m0 707.669333a323.669333 323.669333 0 1 1 0-647.338666 323.669333 323.669333 0 0 1 0 647.338666z m-0.085333-248.106666c5.034667-12.245333 12.16-29.866667 21.333333-52.906667 9.216-23.04 16.682667-42.496 22.485333-58.368 5.802667-15.872 8.704-26.197333 8.704-30.976 0-8.106667-5.162667-12.16-15.488-12.16-11.861333 0-29.354667 6.656-52.565333 19.968-23.168 13.312-48.213333 31.402667-75.221333 54.272l13.994666 12.8 10.24-8.405333c6.272-6.016 13.824-11.690667 22.656-16.981334 8.832-5.290667 15.146667-7.936 18.944-7.936 5.290667 0 7.936 2.56 7.936 7.765334 0 1.450667-6.954667 19.626667-20.821333 54.485333-13.866667 34.858667-21.674667 54.698667-23.466667 59.434667l-27.989333 64.426666c-7.296 18.688-10.965333 32.085333-10.965333 40.192 0 5.376 2.133333 9.813333 6.442666 13.354667 4.266667 3.541333 9.216 5.290667 14.762667 5.290667 13.866667 0 33.536-7.253333 58.965333-21.674667 25.472-14.464 49.664-30.72 72.533334-48.810667l-14.677334-13.44-5.248 3.285334a72.533333 72.533333 0 0 1-5.674666 3.285333c-24.96 17.834667-42.24 26.752-51.797334 26.752-5.077333 0-7.594667-2.56-7.594666-7.765333 0-4.138667 3.797333-15.957333 11.349333-35.498667 7.552-19.498667 14.592-36.266667 21.162667-50.389333zM560.981333 298.666667c-9.258667 0-17.664 2.517333-25.173333 7.552a55.466667 55.466667 0 0 0-17.493333 18.346666c-4.266667 7.253333-6.314667 14.933333-6.314667 23.04 0 9.301333 3.456 17.706667 10.410667 25.173334a32.981333 32.981333 0 0 0 25.088 11.221333c13.013333 0 24.576-5.546667 34.688-16.554667 10.069333-11.008 15.146667-22.912 15.146666-35.669333a30.208 30.208 0 0 0-10.837333-23.552 37.376 37.376 0 0 0-25.557333-9.557333z" fill="#ffffff" p-id="4999"></path></svg></i><span>${i18n.registrationPanelRotate}</span>`);
    const numInput = makeElement('input',['if-rotate','comminput'],{type: 'number', min: '-180',max: '180',value: '0', step: '0.01'});
    const range = new RangeWidget(this.rotateVal,{min: -180, max: 180, step: 0.01, name: 'ifRotate'}, true);
    const scale = makeElement('div',['register-scale-div'],{});
    const xD = makeElement('div',['register-scale']);
    const yD = makeElement('div',['register-scale']);
    const scaleXtext = makeElement('div',['register-scale-text'],{'style':'vertical-align: middle;line-height: 1.875rem;'},`<i title=${i18n.registrationPanelInfo}><svg t="1672814412090" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4998" width="16" height="16"><path d="M512 128a384 384 0 1 0 0 768 384 384 0 0 0 0-768z m0 707.669333a323.669333 323.669333 0 1 1 0-647.338666 323.669333 323.669333 0 0 1 0 647.338666z m-0.085333-248.106666c5.034667-12.245333 12.16-29.866667 21.333333-52.906667 9.216-23.04 16.682667-42.496 22.485333-58.368 5.802667-15.872 8.704-26.197333 8.704-30.976 0-8.106667-5.162667-12.16-15.488-12.16-11.861333 0-29.354667 6.656-52.565333 19.968-23.168 13.312-48.213333 31.402667-75.221333 54.272l13.994666 12.8 10.24-8.405333c6.272-6.016 13.824-11.690667 22.656-16.981334 8.832-5.290667 15.146667-7.936 18.944-7.936 5.290667 0 7.936 2.56 7.936 7.765334 0 1.450667-6.954667 19.626667-20.821333 54.485333-13.866667 34.858667-21.674667 54.698667-23.466667 59.434667l-27.989333 64.426666c-7.296 18.688-10.965333 32.085333-10.965333 40.192 0 5.376 2.133333 9.813333 6.442666 13.354667 4.266667 3.541333 9.216 5.290667 14.762667 5.290667 13.866667 0 33.536-7.253333 58.965333-21.674667 25.472-14.464 49.664-30.72 72.533334-48.810667l-14.677334-13.44-5.248 3.285334a72.533333 72.533333 0 0 1-5.674666 3.285333c-24.96 17.834667-42.24 26.752-51.797334 26.752-5.077333 0-7.594667-2.56-7.594666-7.765333 0-4.138667 3.797333-15.957333 11.349333-35.498667 7.552-19.498667 14.592-36.266667 21.162667-50.389333zM560.981333 298.666667c-9.258667 0-17.664 2.517333-25.173333 7.552a55.466667 55.466667 0 0 0-17.493333 18.346666c-4.266667 7.253333-6.314667 14.933333-6.314667 23.04 0 9.301333 3.456 17.706667 10.410667 25.173334a32.981333 32.981333 0 0 0 25.088 11.221333c13.013333 0 24.576-5.546667 34.688-16.554667 10.069333-11.008 15.146667-22.912 15.146666-35.669333a30.208 30.208 0 0 0-10.837333-23.552 37.376 37.376 0 0 0-25.557333-9.557333z" fill="#ffffff" p-id="4999"></path></svg></i><span>${i18n.registrationPanelScalex}</span>`);
    const scaleXInput = makeElement('input',['comminput','if-scalex'],{type: 'number', 'min': '-99.99','max': '500', 'step': '0.01',value: '0'});
    const scaleYtext = makeElement('div',['register-scale-text'],{'style':'vertical-align: middle;line-height: 1.875rem;'},`<i title=${i18n.registrationPanelInfo}><svg t="1672814412090" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4998" width="16" height="16"><path d="M512 128a384 384 0 1 0 0 768 384 384 0 0 0 0-768z m0 707.669333a323.669333 323.669333 0 1 1 0-647.338666 323.669333 323.669333 0 0 1 0 647.338666z m-0.085333-248.106666c5.034667-12.245333 12.16-29.866667 21.333333-52.906667 9.216-23.04 16.682667-42.496 22.485333-58.368 5.802667-15.872 8.704-26.197333 8.704-30.976 0-8.106667-5.162667-12.16-15.488-12.16-11.861333 0-29.354667 6.656-52.565333 19.968-23.168 13.312-48.213333 31.402667-75.221333 54.272l13.994666 12.8 10.24-8.405333c6.272-6.016 13.824-11.690667 22.656-16.981334 8.832-5.290667 15.146667-7.936 18.944-7.936 5.290667 0 7.936 2.56 7.936 7.765334 0 1.450667-6.954667 19.626667-20.821333 54.485333-13.866667 34.858667-21.674667 54.698667-23.466667 59.434667l-27.989333 64.426666c-7.296 18.688-10.965333 32.085333-10.965333 40.192 0 5.376 2.133333 9.813333 6.442666 13.354667 4.266667 3.541333 9.216 5.290667 14.762667 5.290667 13.866667 0 33.536-7.253333 58.965333-21.674667 25.472-14.464 49.664-30.72 72.533334-48.810667l-14.677334-13.44-5.248 3.285334a72.533333 72.533333 0 0 1-5.674666 3.285333c-24.96 17.834667-42.24 26.752-51.797334 26.752-5.077333 0-7.594667-2.56-7.594666-7.765333 0-4.138667 3.797333-15.957333 11.349333-35.498667 7.552-19.498667 14.592-36.266667 21.162667-50.389333zM560.981333 298.666667c-9.258667 0-17.664 2.517333-25.173333 7.552a55.466667 55.466667 0 0 0-17.493333 18.346666c-4.266667 7.253333-6.314667 14.933333-6.314667 23.04 0 9.301333 3.456 17.706667 10.410667 25.173334a32.981333 32.981333 0 0 0 25.088 11.221333c13.013333 0 24.576-5.546667 34.688-16.554667 10.069333-11.008 15.146667-22.912 15.146666-35.669333a30.208 30.208 0 0 0-10.837333-23.552 37.376 37.376 0 0 0-25.557333-9.557333z" fill="#ffffff" p-id="4999"></path></svg></i><span>${i18n.registrationPanelScaley}</span>`);
    const scaleYInput = makeElement('input',['comminput','if-scaley'],{type: 'number', 'min': '-99.99','max': '500', 'step': '0.01',value: '0'});
    const baifenbi = makeElement('span',['register-scale-baifenbi'],{},`%`)
    const baifenbi1 = makeElement('span',['register-scale-baifenbi'],{},`%`)
    scale.appendChild(xD);
    xD.appendChild(scaleXtext);
    xD.appendChild(scaleXInput);
    xD.appendChild(baifenbi1);
    scale.appendChild(yD);
    yD.appendChild(scaleYtext);
    yD.appendChild(scaleYInput);
    yD.appendChild(baifenbi);
    rotateDom.appendChild(span);
    span.appendChild(text);
    span.appendChild(numInput);
    rotateDom.appendChild(range.element);
    this.scaleDom.appendChild(rotateDom);
    this.scaleDom.appendChild(scale);
    this.transformUI.appendChild(this.scaleDom);
    const initInputElement = (el: HTMLInputElement) => {
      this.registerEventListener(el, 'change', ()=> {
        if(el.valueAsNumber > Number(el.max)){
          el.valueAsNumber = Number(el.max);
        } else if(el.valueAsNumber < Number(el.min)){
          el.valueAsNumber = Number(el.min);
        }
        if(isNaN(el.valueAsNumber))el.valueAsNumber = 0;
        const bound = this.getImageBoun();
        const scaleX = (scaleXInput as HTMLInputElement).valueAsNumber;
        const scaleY = (scaleYInput as HTMLInputElement).valueAsNumber;
        registParam.scalex = 1 + scaleX / 100;
        registParam.scaley = 1 + scaleY / 100;
        registParam.bound = bound!;
        // viewer.newLayerRotate((numInput as HTMLInputElement).valueAsNumber, scaleX, scaleY, bound, registFlipCount)
        viewer.newLayerRotate();
      })
    };
    initInputElement(scaleXInput as HTMLInputElement);
    initInputElement(scaleYInput as HTMLInputElement);
    this.registerEventListener(numInput, 'change', () => {
      const elNum = (numInput as HTMLInputElement).valueAsNumber;
      if(elNum > Number((numInput as HTMLInputElement).max) || elNum < Number((numInput as HTMLInputElement).min )) (numInput as HTMLInputElement).valueAsNumber = 0;
      const bound = this.getImageBoun();
      registParam.bound = bound!;
      if(isNaN((numInput as HTMLInputElement).valueAsNumber))(numInput as HTMLInputElement).valueAsNumber = 0;
      this.rotateVal.value = (numInput as HTMLInputElement).valueAsNumber;
      registParam.deg = this.rotateVal.value;
      viewer.newLayerRotate();
      // viewer.newLayerRotate((numInput as HTMLInputElement).valueAsNumber, (scaleXInput as HTMLInputElement).valueAsNumber, (scaleYInput as HTMLInputElement).valueAsNumber, bound, registFlipCount)
    });
    this.rotateVal.changed.add(()=>{
      const bound = this.getImageBoun();
      (numInput as HTMLInputElement).valueAsNumber = this.rotateVal.value;
      registParam.bound = bound!;
      registParam.deg = this.rotateVal.value;
      viewer.newLayerRotate();
      // viewer.newLayerRotate(rotate.value, (scaleXInput as HTMLInputElement).valueAsNumber, (scaleYInput as HTMLInputElement).valueAsNumber, bound, registFlipCount)
    })
  }
  // 添加位移大小
  displacement_num() {
    const span = makeElement('span', [], {}, i18n.registrationPanelStepsize);
    const input = makeElement('input', ['comminput','transform-size-value'], {'type': 'number','min' : '1', 'value': `${viewer.transformsize}` });
    input.addEventListener('keyup', (e) => {
      if (e.code.toLocaleLowerCase() === 'enter') {
        if ((input as HTMLInputElement).value === '' || (input as HTMLInputElement).value === null || (input as HTMLInputElement).value === undefined) {
          viewer.transformsize = (input as HTMLInputElement).value = '5';
          new message(i18n.registrationPanelStepsizeEmpty, 'warning');
        } else {
          viewer.transformsize = (input as HTMLInputElement).value;
        }
      }
    });
    input.addEventListener('blur', () => {
      if ((input as HTMLInputElement).value === '' || (input as HTMLInputElement).value === null || (input as HTMLInputElement).value === undefined) {
        viewer.transformsize = (input as HTMLInputElement).value = '5';
        new message(i18n.registrationPanelStepsizeEmpty, 'warning');
      } else {
        viewer.transformsize = (input as HTMLInputElement).value;
      }
    });
    this.displacement_num_div.appendChild(span);
    this.displacement_num_div.appendChild(input);
    this.transformUI.appendChild(this.displacement_num_div);
    this.transformUI.appendChild(this.displacement_num_driveDiv);
    drive.forEach((item) => {
      var dirBtn = makeElement('div', ['transform-direction-btn','neuroglancer-icon'], {},svg_shang);
      this.displacement_num_driveDiv.appendChild(dirBtn);
      dirBtn.addEventListener('click', (e:Event) => {
        e.stopPropagation();
        e.preventDefault();
        this.registerMoveUpDown(item.id);
      });
    });
    const isFineTurning = makeElement('div',['isFineTurning','neuroglancer-icon','btn-bubbles'],{'title': i18n.registrationPanelTune},svg_turning);
    isFineTurning.addEventListener('click',()=>{
      registParam.isFineTurning = !registParam.isFineTurning;
      registParam.isFineTurning ? isFineTurning.classList.add('isFineTurning-checked') : isFineTurning.classList.remove('isFineTurning-checked');
    });
    this.displacement_num_driveDiv.appendChild(isFineTurning);
  }
  // 提交配准任务
  displace_move() {
    this.operateDom.appendChild(this.displacement_save);
    const layer_sn = viewer.layerManager.managedLayers.filter((item)=> item.heatmap && item.visible)[0];
    this.displacement_save.addEventListener('click', async () => {
      if(registParam.bound){
        this.displacement_save.classList.add('submit_btn_loading');
        let filename = "";
        if (local_href_param.flag === 'tool') {
          let sn = await getRegisterLayer('sampleid');
          filename = `${sn}.${getData()}.regist.json`;
        } else {
          filename = `${local_href_param.sn}.${getData()}.regist.json`;
        }
        let res = await this.getLayers(`${local_host_SN_flag}/task/regist`, filename, layer_sn);
        const workflow_param = await getWorkflowParam('register', undefined, undefined, layer_sn.name);
        if (res.code === 0) {
          const processid = workflow_param.wfTag;
          if(processTags.includes(processid)){
            // 投workflow
            new message(`${i18n.registrationPanelFilePath}${i18n.registrationPanelDataManagement}/Files/ManualData/StereoMap/Register/${filename}`, 'success', true)
          }else if(local_href_param.flag === 'tool' || (allToolsButton.genelistName as string).toLocaleLowerCase().includes('metabon')){
            // 入仓
            new message(`${i18n.registrationPanelFilePath}${i18n.registrationPanelDataManagement}/Files/ManualData/StereoMap/Register/${filename},${i18n.registrationPanelTask}`, 'success', true)
          }else{
            // 自动化
            new message(i18n.lassoPanelTask + res.data.task_id, 'success')
          }
          this.displacement_save.classList.remove('submit_btn_loading');
          if(processTags.includes(processid)){
            let register_param = await this.get_register_param(layer_sn);
            const sampleId = allToolsButton.iprFileSN || (local_href_param.flag === 'tool' ? layer_sn.genesn : register_param.sampleId)
            let param:any = {}
            let register_key: string = 'register';
            // SAW_spatial_scRNA_V1跟V7流程公用子流程
            if (processid === 'saw-st:v7' || processid === 'SAW_spatial_scRNA_V1') {
              register_key = 'register_v7';
            } else if (processid === 'pt:v1') {
              register_key = 'pt_v1';
            }
            param[register_key] = {
              src: 'SteroMapV3',
              type: 'params',
              originTaskId: register_param.originTaskId,
              sampleId: sampleId,
              value: {
                SN: sampleId,
                RegistJson: '/Files/ManualData/StereoMap/Register/' + filename
              }
            }
            viewer.workflowManualtoolsDialog = new registerLassoOpenDialog('register', param)
          }
          deleteSignpoint();
          this.switchTurnOff();
          isSelectView(true, 'layer-view');
          document.getElementsByClassName('neuroglancer-icon-transform')[0]?.setAttribute('data-checked', 'false');
          document.getElementsByClassName('neuroglancer-icon-standardAnalysis')[0]?.setAttribute('data-checked', 'false');
          isToolControl('remove', ['neuroglancer-icon-transform']);
          document.body.removeChild((document.body.querySelector(".can_zoom_move_container") as HTMLDivElement));
          document.getElementsByClassName('lassoBtn')[0]?.classList.remove('disabled');
          viewer.configDialog?.close();
          viewer.imageSourceModelMat4 = mat4.create();
          viewer.imageSourceModelMat4Inverse = mat4.create();
        } else {
          new message(`${i18n.asyncfail}${res.msg}`, 'error');
        }
      }
    });
  }
  transfomr_reset() {
    const resetEle = makeElement('div', ['neuroglancer-transform-submit', 'neuroglancer-icon'], {}, i18n.reset);
    this.operateDom.appendChild(resetEle);
    resetEle.addEventListener('click', () => {
      viewer.transformsize = 5;
      (document.getElementsByClassName('transform-size-value')[0] as HTMLInputElement).value = '5';
      (document.getElementsByClassName('if-rotate')[0] as HTMLInputElement).valueAsNumber = 0;
      (document.getElementsByClassName('if-scalex')[0] as HTMLInputElement).valueAsNumber = 0;
      (document.getElementsByClassName('if-scaley')[0] as HTMLInputElement).valueAsNumber = 0;
      registParam.OffsetX = 0;
      registParam.OffsetY = 0;
      registParam.filp = 0;
      registParam.deg = 0;
      registParam.isFineTurning = false;
      registParam.scalex = 1;
      registParam.scaley = 1;
      registParam.bound = this.getImageBoun()!;
      this.cur_selectedLayer.opacity.value = 1.0;
      this.cur_selectedLayer.shaderControlState.reset();
      this.element.innerHTML = '';
      for (const control of LAYER_CONTROLS) {
        if(document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'true')continue
        this.element.appendChild(addLayerControlToOptionsTab(this, this.cur_selectedLayer, this.visibility, control));
      }
      this.element.appendChild(
        this
          .registerDisposer(new ShaderControls(
            this.cur_selectedLayer.shaderControlState, this.cur_selectedLayer.manager.root.display, this.cur_selectedLayer, {
            visibility: this.visibility,
            legendShaderOptions: this.cur_selectedLayer.getLegendShaderOptions(),
          }))
          .element);
      if((this.cur_selectedLayer.dataSources[0].loadState_ as LoadedLayerDataSource).transform.value.rank === 3)(this.cur_selectedLayer.dataSources[0].loadState_ as LoadedLayerDataSource).transform.transform = new Float64Array(mat4.create());
      if((this.cur_selectedLayer.dataSources[0].loadState_ as LoadedLayerDataSource).transform.value.rank === 4)(this.cur_selectedLayer.dataSources[0].loadState_ as LoadedLayerDataSource).transform.transform = new Float64Array([1,0,0,0,0,0,1,0,0,0,0,0,1,0,0,0,0,0,1,0,0,0,0,0,1]);
      this.rotateVal.value = 0;
      this.resetSignPoint();
    })
    // this.transformUI.appendChild(this.operateDom);
  }
  resetSignPoint(){
    // 删除特征点
    const source:LoadedDataSubsource = ((this.cur_selectedLayer as ImageUserLayer).dataSources[0].loadState as LoadedLayerDataSource).subsources[2];
    const bounds = this.getImageBoun()!;
          const transform_o_x = 0.5 * (bounds.upperBounds[0] + bounds.lowerBounds[0]);
          const transform_o_y = 0.5 * (bounds.upperBounds[1] + bounds.lowerBounds[1]);
    const linex = makeSingleLineAnnotation(
      new Float32Array([transform_o_x - 5000, transform_o_y, 0]), 
      new Float32Array([transform_o_x + 5000, transform_o_y, 0]), 'line_x');
    linex.properties = [packColor(vec4.fromValues(1.0, 0.0, 0.0, 1.0))];
    // linex.properties = [0,0,1]
    const liney = makeSingleLineAnnotation(
      new Float32Array([transform_o_x, transform_o_y - 5000, 0]), 
      new Float32Array([transform_o_x, transform_o_y + 5000, 0]), 'line_y');
    liney.properties = [packColor(vec4.fromValues(0.0, 1.0, 0.0, 1.0))];
    // x 标注
    const arrowx1 = makeSingleLineAnnotation(
      new Float32Array([transform_o_x + 5400, transform_o_y + 200, 0]), 
      new Float32Array([transform_o_x + 5200, transform_o_y - 200, 0]), 'arrowx1');
    arrowx1.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
    const arrowx2 = makeSingleLineAnnotation(
      new Float32Array([transform_o_x + 5200, transform_o_y + 200, 0]), 
      new Float32Array([transform_o_x + 5400, transform_o_y - 200, 0]), 'arrowx2');
    arrowx2.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
    const arrowy1 = makeSingleLineAnnotation(
      new Float32Array([transform_o_x + 100, transform_o_y - 5600, 0]), 
      new Float32Array([transform_o_x, transform_o_y - 5400, 0]), 'arrowy1');
    arrowy1.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
    const arrowy2 = makeSingleLineAnnotation(
      new Float32Array([transform_o_x - 100, transform_o_y - 5600, 0]), 
      new Float32Array([transform_o_x, transform_o_y - 5400, 0]), 'arrowy2');
    arrowy2.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
    const arrowy3 = makeSingleLineAnnotation(
      new Float32Array([transform_o_x, transform_o_y - 5400, 0]), 
      new Float32Array([transform_o_x, transform_o_y - 5200, 0]), 'arrowy3');
    arrowy3.properties = [packColor(vec4.fromValues(1.0, 1.0, 0.0, 1.0))];
    if(source?.subsourceEntry?.subsource?.staticAnnotations?.references){
      for (let value_ssDNA of source?.subsourceEntry?.subsource?.staticAnnotations?.references) {
        const reference = source.subsourceEntry.subsource.staticAnnotations.getReference(value_ssDNA[0]);
        if(value_ssDNA[0] === 'line_x'){
          source.subsourceEntry.subsource.staticAnnotations?.update(reference, linex);
        }else if(value_ssDNA[0] === 'line_y'){
          source.subsourceEntry.subsource.staticAnnotations?.update(reference, liney);
        }else if(value_ssDNA[0] === 'arrowx1'){
          source.subsourceEntry.subsource.staticAnnotations?.update(reference, arrowx1);
        }else if(value_ssDNA[0] === 'arrowx2'){
          source.subsourceEntry.subsource.staticAnnotations?.update(reference, arrowx2);
        }else if(value_ssDNA[0] === 'arrowy1'){
          source.subsourceEntry.subsource.staticAnnotations?.update(reference, arrowy1);
        }else if(value_ssDNA[0] === 'arrowy2'){
          source.subsourceEntry.subsource.staticAnnotations?.update(reference, arrowy2);
        }else if(value_ssDNA[0] === 'arrowy3'){
          source.subsourceEntry.subsource.staticAnnotations?.update(reference, arrowy3);
        }
      }
    }
  }
  switchTurnOff() {
    removeFromParent(this.rotate);
    removeFromParent(this.filp);
    removeFromParent(this.displacement_num_div);
    removeChildren(this.displacement_num_div);
    removeChildren(this.displacement_num_driveDiv);
    removeChildren(this.displacement_save);
    registParam = {
      OffsetX: 0,
      OffsetY: 0,
      filp: 0,
      deg: 0,
      scalex: 1,
      scaley: 1,
      isFineTurning: false,
      bound: {}
    }
    // registFlipCount = 0;
    // path = '';
    viewer.transformsize = 5;
    document.getElementsByClassName('neuroglancer-icon-transform')[0]?.setAttribute('data-checked', 'false');
    document.getElementsByClassName('neuroglancer-icon-transform')[0] ? document.getElementsByClassName('neuroglancer-icon-transform')[0].setAttribute('submint', 'true') : '';
    (this.cur_selectedLayer.dataSources[0].loadState_ as LoadedLayerDataSource).transform.transform = new Float64Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
    let _json = viewer.state.toJSON();
    let _layers = viewer.state.toJSON().layers;
    // _layers.forEach((item: any) => {
    //   if (item.type === 'image' && item.name === 'ssDNA') {
    //     item.source = item.source.constructor === Object ? item.source.url.replace('transform', 'ssDNA') : item.source.replace('transform', 'ssDNA');
    //   }else if(item.type === 'annotation'){
    //     item.shaderControls = {
    //       'oFactor': 1.0
    //     }
    //   }
    // });
    _layers = viewer.layerCloneObj.layer;
    viewer.clearActiveTool();
    let has_image = false;
    _layers.forEach((item:any, index: number)=>{if(item.name === 'template'){_layers.splice(index ,1);}});
    if (document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('isssDNA') === 'false') {
      _layers.splice(_layers.length - 1, 1);
    }
    _json.layers = _layers;
    _json.layers.forEach((item:any, index:number)=>{
      if(item.name === 'template'){
        _json.layers.splice(index, 1)
      }
      if(item.genetype === 'proteinimage'){
        has_image = true;
      }
    });
    const record_position = JSON.parse(JSON.stringify( _json.position ));
    _json.layout = viewer.layerCloneObj.layout;
    allToolsButton.genelistFile = viewer.layerCloneObj.gene_list_file;
    viewer.state.restoreState(_json);
    disableStatus(false);
    if(!has_image){
      let dimension = _json.dimensions;
      dimension['z'] = _json.dimensions.x;
      viewer.coordinateSpace.restoreState(dimension);
      if(_json.position.length < 1)viewer.position.restoreState(record_position)
    }
  }
  async getLayers(url: string, filename: string, layer: any) {
    const processid = (await getWorkflowParam('register')).wfTag;
    const data = await this.get_register_param(layer);
    if(processTags.includes(processid)){
      for(let key in data.analyseParam) {
        if (key === "scaleX") {
          data["extrude_x"] = data.analyseParam[key];
        } else if (key === "scaleY") {
          data["extrude_y"] = data.analyseParam[key];
        } else {
          data[key] = data.analyseParam[key];
        }
      }
      if (processid === 'pt:v1') {
        data.process_type = processid;
      }
      delete data.analyseParam;
      // 入仓
      const data_warehouse_param = {
        project_id: local_href_param.projectId,
        filename: filename,
        path: '/Files/ManualData/StereoMap/Register/',
        data: data,
        sample_id: data.sampleId
      }
      let json = {
        method: 'post',
        body: JSON.stringify(data_warehouse_param),
        headers: { "Content-Type": "application/json" }
      }
      viewer.setOriginToImageCenter();
      let req = async (): Promise<any> => {
        return await cancellableFetchSpecialOk(undefined, `${local_host_SN_flag}/data_warehouse`, json, responseJson);
      };
      return await req();
    }else{
      data.filename = filename;
      data.sample_id = data.sampleId;
      if(local_href_param.flag === 'tool' || (allToolsButton.genelistName as string).toLocaleLowerCase().includes('metabon')){
        for(let key in data.analyseParam) {
          if (key === "scaleX") {
            data["extrude_x"] = data.analyseParam[key];
          } else if (key === "scaleY") {
            data["extrude_y"] = data.analyseParam[key];
          } else {
            data[key] = data.analyseParam[key];
          }
        }
        delete data.analyseParam;
      }
      // 代谢组数据入仓
      if ((allToolsButton.genelistName as string).toLocaleLowerCase().includes('metabon')) {
        data.is_warehouse = true;
      }
      let json = {
        method: 'post',
        body: JSON.stringify(data),
        headers: { "Content-Type": "application/json" }
      }
      viewer.setOriginToImageCenter();
      let req = async (): Promise<any> => {
        return await cancellableFetchSpecialOk(undefined, `${url}`, json, responseJson);
      };
      return await req();
    }
  }

  async get_register_param(layer: any){
    let data:any = {};
    registParam.bound = this.getImageBoun() as {};
    const if_param = this.getRegisterMode();
    const workflow_param = local_href_param.flag === 'tool' ? await getWorkflowParam('', '', await getRegisterLayer('taskid')) : await getWorkflowParam('register');
    data.projectId = allToolsButton.projectId as string;            //  项目id
    data.zone = local_href_param.zone;
    data.originTaskId = local_href_param.flag === 'tool' ? await getRegisterLayer('taskid') : workflow_param.taskId || '';
    data.sampleId = local_href_param.flag === 'tool' ? await getRegisterLayer('sampleid') : workflow_param.sampleId || '';
    layer.processid = layer.processid ? await getRegisterLayer('processid') : workflow_param.wfName as string || '';
    data.taskType = 'sap_v2_reregister';
    data.version = '1.0.0';                                     //  版本号
    data.analyseParam = {
      offsetX: registParam.OffsetX.toString(),                  //  x位移量
      offsetY: registParam.OffsetY.toString(),                  //  y位移量
      flip: registParam.filp.toString(),                        //  是否翻转
      rot: if_param.rotate,                                     //  旋转角度
      rotate: if_param.rotate,                                  //  旋转角度
      isTuned: registParam.isFineTurning.toString(),            //  是否高精度配准
      scaleX: if_param.extrude_x,                               //  x缩放
      scaleY: if_param.extrude_y,                               //  Y缩放
      // extrude_x: if_param.extrude_x,                            //  x缩放
      // extrude_y: if_param.extrude_y,                            //  Y缩放
      layer: if_param.layer,                                    //  配准图像名称（ ssDNA/DAPI/HE ）
    }
    return data
  }
}


export function getSwitch() {
  let colorReversedElement = document.createElement('div');
  let divElement = document.createElement('div');
  let switchspan = document.createElement('span');
  let ReversedText = document.createElement('span');
  let inputElement = document.createElement('input');
  ReversedText.innerText = 'turn on';
  ReversedText.classList.add('regist-switch-text');
  switchspan.classList.add('regist-switch-span');
  ReversedText.style.paddingRight = '10px';
  colorReversedElement.classList.add('container');
  divElement.style.fontSize = 'auto';
  divElement.classList.add('el-switch');
  inputElement.classList.add('el-switch__input');
  switchspan.classList.add('el-switch__core');
  colorReversedElement.appendChild(divElement);
  divElement.appendChild(ReversedText);
  divElement.appendChild(inputElement);
  divElement.appendChild(switchspan);
  return { colorReversedElement, divElement, switchspan, ReversedText };
}

class ShaderCodeOverlay extends Overlay {
  codeWidget = this.registerDisposer(makeShaderCodeWidget(this.layer));
  constructor(public layer: ImageUserLayer) {
    super();
    this.content.classList.add('neuroglancer-image-layer-shader-overlay');
    this.content.appendChild(this.codeWidget.element);
    this.codeWidget.textEditor.refresh();
  }
}

registerLayerType(ImageUserLayer);
registerVolumeLayerType(VolumeType.IMAGE, ImageUserLayer);
// Use ImageUserLayer as a fallback layer type if there is a `volume` subsource.
registerLayerTypeDetector(subsource => {
  const { volume } = subsource;
  if (volume === undefined) return undefined;
  if (volume.volumeType !== VolumeType.UNKNOWN) return undefined;
  return { layerConstructor: ImageUserLayer, priority: -100 };
});

registerLayerShaderControlsTool(
  ImageUserLayer, layer => ({
    shaderControlState: layer.shaderControlState,
    legendShaderOptions: layer.getLegendShaderOptions()
  }));
